import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Paper, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Button,
    Chip,
    TextField,
    InputAdornment
} from '@mui/material';
import { 
    ContactPage, 
    FileDownload, 
    Search,
    Refresh
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchToClient } from '../../data/slices/toClientSlice';
import { COLORS } from '../../settings/themeSettings';
import PageHeader from '../../components/layout/PageHeader';

const ClientCodesPage = () => {
    const dispatch = useDispatch();
    const { toClients } = useSelector((state) => state.toClients);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        dispatch(fetchToClient());
    }, [dispatch]);

    const filteredClients = (toClients || []).filter(c => 
        (c.libelle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.code || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExport = () => {
        // Logique d'export CSV simplifiée
        const headers = ['Libellé', 'Code Client', 'Email', 'Téléphone', 'Ville'];
        const rows = filteredClients.map(c => [
            c.libelle,
            c.code,
            c.email || '—',
            c.telephone || '—',
            c.ville || '—'
        ]);
        
        let csvContent = "data:text/csv;charset=utf-8," 
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "codes_clients_aft.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title="Codes Clients & Tiers"
                subtitle="Consultez et exportez les codes dédiés pour la comptabilité et le suivi tiers"
                icon={<ContactPage />}
                breadcrumbs={[
                    { label: 'Utilitaires', path: '/utilitaires' },
                    { label: 'Codes Clients', path: '/utilitaires/codes-clients' }
                ]}
                actions={[
                    { 
                        label: "Exporter CSV", 
                        icon: <FileDownload />, 
                        onClick: handleExport,
                        color: 'secondary'
                    }
                ]}
            />

            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200] }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Rechercher par nom ou code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: COLORS.gray[400] }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <Button size="small" onClick={() => setSearchTerm('')} startIcon={<Refresh />}>
                                    Vider
                                </Button>
                            </InputAdornment>
                        )
                    }}
                />
            </Paper>

            <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200] }}>
                <Table>
                    <TableHead sx={{ bgcolor: COLORS.gray[50] }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>PARTENAIRE / CLIENT</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>CODE COMPTABLE</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>CONTACT</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>LOCALISATION</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredClients.map((client) => (
                            <TableRow key={client.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{client.libelle}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={client.code || 'NON DÉFINI'} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: client.code ? COLORS.secondary + '15' : COLORS.gray[100], 
                                            color: client.code ? COLORS.secondary : COLORS.gray[500],
                                            fontWeight: 800,
                                            fontFamily: 'monospace'
                                        }} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{client.email || '—'}</Typography>
                                    <Typography variant="caption" color="text.secondary">{client.telephone || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{client.ville || '—'}</Typography>
                                    <Typography variant="caption" color="text.secondary">{client.pays || 'Sénégal'}</Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ClientCodesPage;
